import React, { FC, useEffect, useMemo, useState } from "react";
import { useStore } from "effector-react";
import { PackageCard } from "../../../cards";
import {
  $selectedPackages,
  SelectedPackage,
  selectedPackagesApi,
} from "../../../store/selected/selectedPackages";
import { $selectedLocation } from "../../../store/selected/selectedLocations";
import { useTranslation } from "react-i18next";
import { PackagesEndpoint } from "../../../store/endpoints";
import {
  Grid,
  Pagination,
  Skeleton,
  Carousel,
  Input,
  Alert,
} from "../../../components";
import { editPackage } from "./EditPackage";
import { $selectedDates } from "../../../store/selected/selectedDateRange";
import {
  $selectedPersonTypes,
  $selectedPersonTypesQty,
} from "../../../store/selected/selectedPersonTypes";
import { AppConfig } from "../../../config";
import { isArrayFilled, isPresent } from "../../../utils";
import { useDebounce, usePageSize } from "../../../hooks";
import { Package } from "../../../store/models";
import { Dinero } from "dinero.js";
import { GridBreakpoints } from "../../../components/Grid";
import { FiSearch } from "react-icons/fi";
import stl from "../DateRange/index.module.scss";

export type PackagesDisplayData = (Package & {
  selectedData?: SelectedPackage;
  pricingData?: {
    max: Dinero;
    min: Dinero;
  };
})[];

const gridBreakpoints: GridBreakpoints = {
  lg: 4,
  md: 6,
  sm: 6,
  xs: 12,
};

const Packages: FC = () => {
  const { t, i18n } = useTranslation(AppConfig.translationFileNames.step_Main);
  const pageSize = usePageSize();

  const { data: packages, pending } = useStore(PackagesEndpoint.list.store);
  const { data: priceRanges, pending: priceRangesPending } = useStore(
    PackagesEndpoint.priceRanges.store
  );
  const [paginationCurrentPage, setPaginationCurrentPage] = useState<number>(1);

  const selectedPackages = useStore($selectedPackages);
  const selectedLocation = useStore($selectedLocation);
  const selectedDates = useStore($selectedDates);
  const selectedPersonTypes = useStore($selectedPersonTypes);
  const selectedPersonTypesQty = useStore($selectedPersonTypesQty);

  const [searchValue, setSearchValue] = useState<string>("");
  const debouncedSearchValue = useDebounce(searchValue, 1000);

  const [viewMode, setViewMode] = useState<"pages" | "carousel">(
    pageSize.md ? "pages" : "carousel"
  );

  useEffect(() => {
    setViewMode(pageSize.md ? "pages" : "carousel");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize.md]);

  useEffect(() => {
    const interval = setInterval(read, AppConfig.forceReloadListItemsByTime);
    return () => clearInterval(interval);
  }, [selectedLocation, selectedDates, selectedPersonTypes]);

  useEffect(() => {
    read();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue, pageSize.md]);

  const read = () => {
    if (
      selectedLocation &&
      isArrayFilled(selectedDates) &&
      isArrayFilled(selectedPersonTypes)
    ) {
      PackagesEndpoint.list.call({
        _query: {
          lang: i18n.language,
          location_id: selectedLocation.id,
          dates: selectedDates,
          person_types: selectedPersonTypes,
          search: searchValue,
        },
      });
    }
  };

  useEffect(() => {
    const handlerTimeout = setTimeout(read, 1000); //debounce for read()
    changePage(1); //reset pagination
    return () => {
      PackagesEndpoint.list.cancel();
      clearTimeout(handlerTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedLocation,
    i18n.language,
    selectedDates,
    debouncedSearchValue,
    selectedPersonTypes,
  ]);

  useEffect(() => {
    if (
      isPresent(packages) &&
      isArrayFilled(packages?.data) &&
      isArrayFilled(selectedDates) &&
      isArrayFilled(selectedPersonTypes)
    ) {
      const handlerTimeout = setTimeout(
        () =>
          PackagesEndpoint.priceRanges.call({
            packages: packages.data,
            personTypes: selectedPersonTypes,
            dates: selectedDates,
          }),
        2000
      );
      return () => {
        PackagesEndpoint.priceRanges.cancel();
        clearTimeout(handlerTimeout);
      };
    }
  }, [packages, selectedPersonTypes, selectedDates]);

  const active = useMemo(() => {
    return (
      selectedPersonTypesQty > 0 &&
      selectedDates &&
      isArrayFilled(selectedDates)
    );
  }, [selectedPersonTypesQty, selectedDates]);

  const changePage = (page: number) => {
    setPaginationCurrentPage(page);
  };

  /** Sublist items packages */
  const displayData = useMemo<PackagesDisplayData>(() => {
    if (!packages || !active) return [];

    const indexOfFirstDisplayData =
      (paginationCurrentPage - 1) * AppConfig.paginationPerPage;
    const indexOfLastDisplayData =
      indexOfFirstDisplayData + AppConfig.paginationPerPage;

    return (viewMode === "pages"
      ? packages.data.slice(indexOfFirstDisplayData, indexOfLastDisplayData)
      : packages.data
    ).map((item) => {
      const selectedPackage = selectedPackages.find(
        ({ entity: { id } }) => id === item.id
      );

      return {
        ...item,
        selectedData: selectedPackage,
        pricingData: priceRanges?.[item.id],
      };
    });
  }, [
    active,
    priceRanges,
    packages,
    selectedPackages,
    paginationCurrentPage,
    viewMode,
  ]);

  return (
    <>
      <Grid
        container
        gutter={15}
        style={{ alignItems: "center", marginTop: "3em", marginBottom: "1em" }}
      >
        <Grid item xs={12} sm={4}>
          <h2
            style={{
              marginBottom: "0.5em",
              ...(active ? {} : { opacity: 0.5 }),
            }}
          >
            {selectedLocation?.packages_title}
          </h2>
        </Grid>

        {active && (
          <Grid item xs={12} sm={4}>
            <Input
              disabled={pending}
              prepend={
                <FiSearch
                  style={{
                    fontSize: "1.1em",
                    margin: "4px 0 0 8px",
                  }}
                />
              }
              className={stl.searchBar}
              variant={"search"}
              type={"search"}
              size={"small"}
              value={searchValue}
              onChange={setSearchValue}
              placeholder={t("searchBarPlaceholder")}
            />
          </Grid>
        )}
        {viewMode === "pages" &&
          active &&
          packages?.data?.length !== undefined &&
          packages?.data?.length > AppConfig.paginationPerPage && (
            <Grid item xs={12} sm={4} style={{ textAlign: "right" }}>
              <Pagination
                disabled={pending}
                showLessItems
                total={packages?.data?.length ?? 0}
                current={paginationCurrentPage}
                pageSize={AppConfig.paginationPerPage}
                onChange={changePage}
              />
            </Grid>
          )}

        <Grid item xs={12} style={{ margin: "1.5em 0" }}>
          {selectedLocation?.packages_description}
        </Grid>
      </Grid>

      {pending && (
        <Skeleton height={250} showIcon>
          {t("activityModal.retrievingTimeSlots")}
        </Skeleton>
      )}

      {!pending && active && viewMode === "pages" && (
        <Grid container gutter={0}>
          {displayData.map((item, idx) => (
            <Grid key={item?.name ?? idx} item {...gridBreakpoints}>
              <PackageCard
                disabled={selectedPersonTypesQty === 0}
                data={item}
                onEdit={editPackage}
                onDelete={({ entity: { id } }) => {
                  selectedPackagesApi.remove({ id });
                }}
                pricePending={priceRangesPending}
                price={item.pricingData}
                selectedData={item.selectedData}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {!pending &&
        active &&
        viewMode === "carousel" &&
        isArrayFilled(displayData) && (
          <Carousel
            style={{ marginLeft: "" }}
            carouselProps={{
              selectedQuantity: selectedPackages.length,
              perPageLoad: 9999,
            }}
          >
            {displayData.map((item, idx) => (
              <PackageCard
                key={item?.name ?? idx}
                disabled={selectedPersonTypesQty === 0}
                data={item}
                onEdit={editPackage}
                onDelete={({ entity: { id } }) => {
                  selectedPackagesApi.remove({ id });
                }}
                pricePending={priceRangesPending}
                price={item.pricingData}
                selectedData={item.selectedData}
              />
            ))}
          </Carousel>
        )}
      {active && !isArrayFilled(displayData) && !searchValue && (
        <p className={stl.noSlots}>{t("noSlots")}</p>
      )}
      {searchValue && !isArrayFilled(displayData) && !pending && (
        <Alert variant={"info"}>{t("nothingFound")}</Alert>
      )}
    </>
  );
};

export default Packages;
